import { useEffect, useMemo, useState } from "react";
import api, { money, shortDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Button, Input, Select, Modal, PageHeader, EmptyState, Badge, StatCard } from "@/components/kit";
import { TID } from "@/constants/testIds";
import { Plus, Trash, Buildings, Car, Paperclip, DownloadSimple } from "@phosphor-icons/react";

const CATEGORY_META = {
  office: { label: "Office", tone: "info", icon: Buildings, suggestions: ["Stationery", "Electricity", "Rent", "Internet", "Water", "Maintenance", "Refreshments", "Agent Commission", "Agent Salary", "Staff Salary", "Other"] },
  travel: { label: "Travel", tone: "warning", icon: Car, suggestions: ["Fuel", "Vehicle Rental", "Public Transport", "Lodging", "Meals", "Toll", "Other"] },
};

const MODES = ["cash", "bank", "upi", "card"];

export default function ExpensesPage() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [category, setCategory] = useState("");
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [receiptFile, setReceiptFile] = useState(null);

  function defaultForm() {
    return {
      category: "office",
      subcategory: "",
      amount: "",
      date: new Date().toISOString().slice(0, 10),
      payee: "",
      mode: "cash",
      description: "",
      from_location: "",
      to_location: "",
      distance_km: "",
    };
  }

  const load = async () => {
    const q = new URLSearchParams();
    if (category) q.set("category", category);
    const [e, s] = await Promise.all([
      api.get(`/expenses?${q.toString()}`),
      api.get("/expenses/summary"),
    ]);
    setExpenses(e.data);
    setSummary(s.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  const canWrite = ["super_admin", "branch_admin", "staff"].includes(user.role);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    const body = { ...form };
    ["amount", "distance_km"].forEach((k) => {
      if (body[k] !== "" && body[k] !== undefined) body[k] = Number(body[k]);
      else delete body[k];
    });
    // trim empties
    Object.keys(body).forEach((k) => { if (body[k] === "") delete body[k]; });
    try {
      const created = await api.post("/expenses", body);
      // If a receipt was attached, upload it now
      if (receiptFile) {
        const fd = new FormData();
        fd.append("file", receiptFile);
        try {
          await api.post(`/expenses/${created.data.id}/receipt`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (rErr) {
          setErr(rErr.response?.data?.detail || "Expense saved, but receipt upload failed.");
        }
      }
      setOpen(false); setForm(defaultForm()); setReceiptFile(null); load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadReceipt = async (expenseId, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErr("Receipt too large. Max 5 MB."); return; }
    setErr("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/expenses/${expenseId}/receipt`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Upload failed");
    }
  };

  const downloadReceipt = async (expense) => {
    try {
      const res = await api.get(
        `/expenses/${expense.id}/receipt/download?disposition=attachment`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `receipt-${expense.id.slice(0, 8)}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      setErr("Download failed");
    }
  };

  const viewReceipt = async (expense) => {
    try {
      const res = await api.get(
        `/expenses/${expense.id}/receipt/download?disposition=inline`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch {
      setErr("Could not open receipt");
    }
  };

  const removeReceipt = async (expense) => {
    if (!window.confirm("Delete the attached receipt?")) return;
    try {
      await api.delete(`/expenses/${expense.id}/receipt`);
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Delete failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await api.delete(`/expenses/${id}`);
    load();
  };

  const catTotals = useMemo(() => {
    const m = { office: 0, travel: 0 };
    (summary?.mtd_by_category || []).forEach((c) => (m[c._id] = c.total));
    return m;
  }, [summary]);

  return (
    <div className="w-full p-4 md:p-8 pb-24 md:pb-8 fade-up">
      <PageHeader
        title="Expenses"
        subtitle="Record office and travel expenses of the society."
        action={
          canWrite && (
            <Button onClick={() => setOpen(true)} data-testid={TID.addBtn("expense")}>
              <Plus size={16} weight="bold" /> Add expense
            </Button>
          )
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Month-to-date total" value={money(summary?.mtd_total || 0)} tone="warn" testId="exp-mtd-total" />
        <StatCard label="Office (MTD)" value={money(catTotals.office)} testId="exp-mtd-office" />
        <StatCard label="Travel (MTD)" value={money(catTotals.travel)} testId="exp-mtd-travel" />
        <StatCard label="All-time entries" value={summary?.all_time_count ?? 0} hint={money(summary?.all_time_total || 0)} testId="exp-all-count" />
      </div>

      <Card className="p-4 mb-4">
        <div className="flex gap-2 flex-wrap items-center">
          {[["", "All"], ["office", "Office"], ["travel", "Travel"]].map(([v, l]) => (
            <button
              key={v || "all"}
              onClick={() => setCategory(v)}
              data-testid={`expense-filter-${v || "all"}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                category === v
                  ? "bg-emerald-700 text-white border-emerald-700"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </Card>

      <Card>
        {expenses.length === 0 ? (
          <EmptyState title="No expenses recorded" subtitle={canWrite ? "Click 'Add expense' to record your first entry." : ""} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Date</th>
                  <th className="text-left px-4 py-3 font-semibold">Category</th>
                  <th className="text-left px-4 py-3 font-semibold">Description</th>
                  <th className="text-left px-4 py-3 font-semibold">Payee</th>
                  <th className="text-left px-4 py-3 font-semibold">Mode</th>
                  <th className="text-right px-4 py-3 font-semibold">Amount</th>
                  <th className="text-center px-4 py-3 font-semibold">Receipt</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {expenses.map((e) => {
                  const meta = CATEGORY_META[e.category];
                  return (
                    <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`expense-row-${e.id}`}>
                      <td className="px-4 py-3 mono text-slate-600">{shortDate(e.date)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Badge tone={meta?.tone || "neutral"}>{meta?.label || e.category}</Badge>
                          {e.subcategory && <span className="text-xs text-slate-500">{e.subcategory}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-800">
                        <div>{e.description || "—"}</div>
                        {e.category === "travel" && (e.from_location || e.to_location) && (
                          <div className="text-xs text-slate-500 mt-0.5">
                            {e.from_location || "?"} → {e.to_location || "?"}{e.distance_km ? ` · ${e.distance_km} km` : ""}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{e.payee || "—"}</td>
                      <td className="px-4 py-3 capitalize text-slate-600">{e.mode || "—"}</td>
                      <td className="px-4 py-3 mono text-right font-medium text-slate-900">{money(e.amount)}</td>
                      <td className="px-4 py-3 text-center">
                        {e.has_receipt ? (
                          <div className="inline-flex items-center gap-1">
                            <button
                              onClick={() => viewReceipt(e)}
                              className="text-emerald-700 hover:text-emerald-900"
                              title="View receipt"
                              data-testid={`view-receipt-${e.id}`}
                            >
                              <Paperclip size={16} weight="bold" />
                            </button>
                            <button
                              onClick={() => downloadReceipt(e)}
                              className="text-slate-500 hover:text-slate-800"
                              title="Download receipt"
                              data-testid={`dl-receipt-${e.id}`}
                            >
                              <DownloadSimple size={16} />
                            </button>
                            {canWrite && (
                              <button
                                onClick={() => removeReceipt(e)}
                                className="text-rose-500 hover:text-rose-700"
                                title="Remove receipt"
                                data-testid={`rm-receipt-${e.id}`}
                              >
                                <Trash size={14} />
                              </button>
                            )}
                          </div>
                        ) : (
                          canWrite ? (
                            <label className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-700 cursor-pointer" data-testid={`attach-receipt-${e.id}`}>
                              <Paperclip size={14} /> Attach
                              <input
                                type="file"
                                accept="image/*,application/pdf"
                                className="hidden"
                                onChange={(ev) => {
                                  const f = ev.target.files?.[0];
                                  ev.target.value = "";
                                  if (f) uploadReceipt(e.id, f);
                                }}
                              />
                            </label>
                          ) : (
                            <span className="text-xs text-slate-300">—</span>
                          )
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {["super_admin", "branch_admin"].includes(user.role) && (
                          <button onClick={() => del(e.id)} data-testid={`del-expense-${e.id}`} className="text-rose-600 hover:text-rose-800">
                            <Trash size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal open={open} onClose={() => { setOpen(false); setReceiptFile(null); }} title="Record expense" maxWidth="max-w-xl">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {["office", "travel"].map((c) => {
              const meta = CATEGORY_META[c];
              const Icon = meta.icon;
              const active = form.category === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm({ ...form, category: c, subcategory: "" })}
                  data-testid={`expense-cat-${c}`}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                    active
                      ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                      : "border-slate-200 text-slate-700 hover:border-slate-300"
                  }`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${active ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`}>
                    <Icon size={18} weight="fill" />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-sm">{meta.label}</div>
                    <div className="text-[11px] text-slate-500">{c === "office" ? "Utilities, supplies, rent" : "Fuel, transport, lodging"}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs font-medium text-slate-600 mb-1.5">Sub-category</div>
              <input
                list="expense-subcat-suggestions"
                data-testid="expense-subcategory"
                value={form.subcategory}
                onChange={(e) => setForm({ ...form, subcategory: e.target.value })}
                placeholder={form.category === "office" ? "e.g. Stationery" : "e.g. Fuel"}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
              />
              <datalist id="expense-subcat-suggestions">
                {CATEGORY_META[form.category].suggestions.map((s) => <option key={s} value={s} />)}
              </datalist>
            </div>
            <Input
              label="Amount (₹)" type="number" step="0.01" required testId="expense-amount"
              value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input label="Date" type="date" required testId="expense-date"
              value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            <Select label="Payment mode" testId="expense-mode" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
              {MODES.map((m) => <option key={m} value={m} className="capitalize">{m.toUpperCase()}</option>)}
            </Select>
          </div>

          <Input label="Payee / Vendor" testId="expense-payee"
            value={form.payee} onChange={(e) => setForm({ ...form, payee: e.target.value })} />

          {form.category === "travel" && (
            <div className="grid grid-cols-3 gap-3">
              <Input label="From" testId="expense-from"
                value={form.from_location} onChange={(e) => setForm({ ...form, from_location: e.target.value })} />
              <Input label="To" testId="expense-to"
                value={form.to_location} onChange={(e) => setForm({ ...form, to_location: e.target.value })} />
              <Input label="Distance (km)" type="number" step="0.1" testId="expense-distance"
                value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} />
            </div>
          )}

          <div>
            <div className="text-xs font-medium text-slate-600 mb-1.5">Description</div>
            <textarea
              data-testid="expense-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
              placeholder="Notes about this expense"
            />
          </div>

          <div>
            <div className="text-xs font-medium text-slate-600 mb-1.5">Attach receipt (optional)</div>
            <div className="flex items-center gap-2 border border-dashed border-slate-300 rounded-lg px-3 py-2.5 bg-slate-50">
              <Paperclip size={16} className="text-slate-400" />
              {receiptFile ? (
                <>
                  <span className="text-sm text-slate-800 flex-1 truncate mono">{receiptFile.name}</span>
                  <span className="text-[11px] text-slate-500 mono">{(receiptFile.size / 1024).toFixed(0)} KB</span>
                  <button
                    type="button"
                    onClick={() => setReceiptFile(null)}
                    className="text-xs text-rose-600 hover:text-rose-800"
                    data-testid="clear-receipt"
                  >
                    Remove
                  </button>
                </>
              ) : (
                <label className="text-sm text-slate-600 flex-1 cursor-pointer">
                  <span className="text-emerald-700 font-medium">Choose a file</span> or take a photo of the bill
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    data-testid="expense-receipt-input"
                    onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                  />
                </label>
              )}
            </div>
            <div className="text-[11px] text-slate-400 mt-1">Image or PDF, max 5 MB. Optional but recommended for audits.</div>
          </div>

          {err && <div className="text-sm text-rose-600">{String(err)}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={() => { setOpen(false); setReceiptFile(null); }} data-testid={TID.cancelBtn}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid={TID.saveBtn}>{saving ? "Saving…" : "Record expense"}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
